// ========================================
// CUSTOM HOOKS
// ========================================

import { useState, useEffect, useCallback } from "react";

/**
 * useAutoSlide – cycles through an array of items at a given interval
 * @param {number} length  - total number of slides
 * @param {number} delay   - milliseconds between transitions (default 4500)
 */
export function useAutoSlide(length, delay = 4500) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % length);
    }, delay);
    return () => clearInterval(timer);
  }, [length, delay]);

  const goTo = useCallback((index) => setCurrent(index), []);

  return { current, goTo };
}

/**
 * useScrolled – returns true once the user scrolls past `threshold` px
 * @param {number} threshold - scroll distance in pixels (default 40)
 */
export function useScrolled(threshold = 40) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  return scrolled;
}

/**
 * useWishlist – manages a local Set of wishlisted product IDs
 */
export function useWishlist() {
  const [wishlist, setWishlist] = useState(new Set());

  const toggle = useCallback((id) => {
    setWishlist((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const has = useCallback((id) => wishlist.has(id), [wishlist]);

  return { toggle, has };
}

/**
 * useCart – manages a simple cart (count only for now)
 */
export function useCart() {
  const [items, setItems] = useState([]);

  const add = useCallback((product) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.id === product.id ? { ...i, qty: i.qty + 1 } : i
        );
      }
      return [...prev, { ...product, qty: 1 }];
    });
  }, []);

  const count = items.reduce((sum, i) => sum + i.qty, 0);

  return { items, add, count };
}
